<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head fair-survey-step__head--centered">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="completion-step__stats">
      <div class="completion-step__stat">
        <span class="completion-step__stat-value">
          {{ completedSteps }}/{{ totalSteps }}
        </span>
        <span class="completion-step__stat-label">Шагов пройдено</span>
      </div>
      <div class="completion-step__stat">
        <span class="completion-step__stat-value">{{ totalMeasurements }}</span>
        <span class="completion-step__stat-label">Измерений собрано</span>
      </div>
      <div class="completion-step__stat">
        <span class="completion-step__stat-value">{{ recommendationsCount }}</span>
        <span class="completion-step__stat-label">Рекомендаций</span>
      </div>
    </div>

    <ul class="completion-step__next">
      <li
        v-for="item in NEXT_STEPS"
        :key="item.title"
        class="completion-step__next-item"
      >
        <span class="completion-step__next-title">{{ item.title }}</span>
        <span class="completion-step__next-text">{{ item.text }}</span>
      </li>
    </ul>

    <div class="completion-step__actions">
      <FairButton
        label="Начать покупки"
        variant="primary"
        size="m"
        :rounded="false"
        @click="startShopping"
      />
      <FairButton
        label="Мой профиль"
        variant="tonal"
        size="m"
        :rounded="false"
        @click="viewProfile"
      />
    </div>

    <p class="fair-survey-step__note">
      Параметры всегда можно обновить в профиле.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { FairButton } from '@/ui/primitives/FairButton'
import { useSurveyModal } from '@/features/survey/store'
import { useRouter } from 'vue-router'
import type { SurveyStep } from '@/features/survey/types/survey-config'

interface Props {
  step: SurveyStep & { type: 'completion' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()

const NEXT_STEPS = [
  {
    title: 'Покупки с умом',
    text: 'В каталоге подсветим товары, которые подойдут по размеру',
  },
  {
    title: 'Персональные уведомления',
    text: 'Сообщим, когда нужный размер появится в наличии',
  },
  {
    title: 'Избранное по размеру',
    text: 'Сохранённые товары отфильтруются по вашим меркам',
  },
]
const router = useRouter()

const completedSteps = computed(() => surveyStore.totalSteps)
const totalSteps = computed(() => surveyStore.totalSteps)

const totalMeasurements = computed(() => {
  const data = surveyStore.surveyModal.surveyData
  let count = 0

  Object.values(data).forEach((value) => {
    if (value !== null && value !== undefined && value !== '') {
      count++
    }
  })

  return count
})

const recommendationsCount = computed(() => {
  if (!surveyStore.surveyModal.results) return 0

  const results = surveyStore.surveyModal.results
  if (!results) return 0

  // Считаем количество категорий с данными
  let count = 0
  if (results.tops) count++
  if (results.bottoms) count++
  if (results.outerwear) count++
  if (results.shoes) count++

  return count
})

function startShopping() {
  surveyStore.hideSurveyModal()
  router.push('/')
}

function viewProfile() {
  surveyStore.hideSurveyModal()
  router.push('/profile')
}
</script>

<style scoped>
.completion-step__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--fair-rd-space-12);
}

.completion-step__stat {
  display: flex;
  flex-direction: column;
  gap: var(--fair-rd-space-4);
  padding: var(--fair-rd-space-16);
  border: 1px solid var(--fair-rd-line-hairline);
  border-radius: var(--fair-rd-radius-card);
}

.completion-step__stat-value {
  font-family: var(--fair-rd-font-display, 'Gilroy', sans-serif);
  font-size: 24px;
  font-weight: 500;
  line-height: 1;
  color: var(--fair-rd-text-primary);
  font-variant-numeric: tabular-nums;
}

.completion-step__stat-label {
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 12px;
  line-height: 1.2;
  color: var(--fair-rd-text-muted);
}

.completion-step__next {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.completion-step__next-item {
  display: flex;
  flex-direction: column;
  gap: var(--fair-rd-space-4);
  padding-block: var(--fair-rd-space-12);
  border-bottom: 1px solid var(--fair-rd-line-hairline);
}

.completion-step__next-item:last-child {
  border-bottom: 0;
}

.completion-step__next-title {
  font-family: var(--fair-rd-font-display, 'Gilroy', sans-serif);
  font-size: 15px;
  font-weight: 500;
  color: var(--fair-rd-text-primary);
}

.completion-step__next-text {
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 14px;
  line-height: 1.4;
  color: var(--fair-rd-text-muted);
}

.completion-step__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--fair-rd-space-8);
}
</style>
